import logging from 'utils/logging';
import {
  GAMESTATE_ROUND,
  GAMESTATE_ROUNDSTATE_DAILYDOUBLE_PICKED,
} from 'features/gamestate/constants';
import { HOST_STATUS, MOMENT_TYPES, NARRATION_KEYS } from '../hostStateSlice';
import { advanceState } from '../../gamestate/GameState';
import { registerCacheReset } from '../momentCacheManager';

const log = logging.getLogger('moments.DailyDoubleReveal');
log.setLevel('info');

const ddRevealed = new Set();
const ddRevealPlayed = new Set();

export const resetDailyDoubleRevealCache = () => {
  ddRevealed.clear();
  ddRevealPlayed.clear();
};
registerCacheReset('round', resetDailyDoubleRevealCache);

export const DailyDoubleRevealMoment = {
  id: 'DAILY_DOUBLE_REVEAL',
  shouldTrigger: ({ currentState, currentPlayers, questions }) => {
    if (currentState?.currentState !== GAMESTATE_ROUND) {
      return false;
    }

    if (currentState?.roundState !== GAMESTATE_ROUNDSTATE_DAILYDOUBLE_PICKED) {
      return false;
    }

    const questionId = currentState?.questionId;
    if (!questionId) {
      return false;
    }

    if (ddRevealed.has(questionId)) {
      return false;
    }

    const question = questions?.find(q => q.id === questionId);
    if (!question) {
      return false;
    }

    const playerId = currentState?.playerInControl;
    const player = currentPlayers?.find(p => p.id === playerId);
    if (!player) {
      return false;
    }

    ddRevealed.add(questionId);

    log.info(`Daily Double revealed: ${questionId} by ${player.name}`);

    // Compute standings context
    const sortedPlayers = [...(currentPlayers || [])].sort((a, b) => (b.score || 0) - (a.score || 0));
    const playerScore = player.score || 0;
    const playerRank = sortedPlayers.findIndex(p => p.id === player.id) + 1;
    const totalPlayers = sortedPlayers.length;

    const leader = sortedPlayers[0];
    const isLeader = leader?.id === player.id;
    const leaderName = leader?.name || '';
    const leaderScore = leader?.score || 0;

    // Points behind leader (0 if is leader)
    const pointsBehindLeader = isLeader ? 0 : leaderScore - playerScore;

    // Lead amount (how far ahead of 2nd place, 0 if not leading)
    let leadAmount = 0;
    if (isLeader && sortedPlayers.length > 1) {
      leadAmount = playerScore - (sortedPlayers[1]?.score || 0);
    }

    // Max wager: max(roundMax, playerScore) where roundMax is $1000 for round1, $2000 for round2+
    const roundMax = currentState.round === 'round1' ? 1000 : 2000;
    const maxWager = Math.max(roundMax, playerScore);

    const narrationKey = `${NARRATION_KEYS.DAILY_DOUBLE_REVEAL}_${questionId}`;

    return {
      shouldTriggerAction: false,
      shouldGenerateNarration: true,
      narrationParams: {
        narrationKey,
        momentType: MOMENT_TYPES.DAILY_DOUBLE_REVEAL,
        context: {
          questionId,
          category: question.category,
          value: question.value,
          playerName: player.name,
          playerScore,
          playerRank,
          totalPlayers,
          isLeader,
          leaderName,
          leaderScore,
          pointsBehindLeader,
          leadAmount,
          maxWager,
        }
      }
    };
  }
};

export const DailyDoubleRevealPlaybackMoment = {
  id: 'DAILY_DOUBLE_REVEAL_PLAYBACK',
  momentType: MOMENT_TYPES.DAILY_DOUBLE_REVEAL,
  shouldPlay: (gameState, hostState, narration, playedNarrations) => {
    if (!narration) {
      return false;
    }

    if (narration.momentType !== MOMENT_TYPES.DAILY_DOUBLE_REVEAL) {
      return false;
    }

    if (gameState?.currentState !== GAMESTATE_ROUND || gameState?.roundState !== GAMESTATE_ROUNDSTATE_DAILYDOUBLE_PICKED) {
      return false;
    }

    const narrationKey = narration?.key;
    if (ddRevealPlayed.has(narrationKey)) {
      return false;
    }

    if (gameState?.questionId !== narration?.context?.questionId) {
      return false;
    }

    if (hostState?.status === HOST_STATUS.SPEAKING) {
      return false;
    }

    const narrationReady = narration.textStatus === "READY" && (narration.audioStatus === "READY" || narration.audioStatus === "ERROR");
    if (!narrationReady) {
      return false;
    }

    ddRevealPlayed.add(narrationKey);
    return true;
  },

  onComplete: async (gameId, dispatch, getState) => {
    log.info('Daily Double reveal narration complete, advancing to wagering');

    try {
      const state = getState();
      const gameObject = state.game?.game;

      if (!gameObject) {
        log.error('Game object not found in state');
        return;
      }

      const players = Object.values(state.player?.entities || {});
      await advanceState(gameObject, players, dispatch);
    } catch (error) {
      log.error('Error advancing game state after DD reveal:', error);
    }
  }
};

const api = {
  trigger: DailyDoubleRevealMoment,
  playback: DailyDoubleRevealPlaybackMoment
};

export default api;
